import Mention from "@tiptap/extension-mention";
import tippy, { Instance, GetReferenceClientRect } from "tippy.js";
import { ReactRenderer, Editor } from "@tiptap/react";
import { PluginKey } from "@tiptap/pm/state";
import { getMentionExtensionStorage, MentionExtensionId } from "../utils";
import { dueDateDecoratorPlugin } from "./suggestionDecorator";
import { findDueDateSuggestionMatch } from "./findDueDateSuggestionMatch";
import { SuggestionEntryComponent } from "~/components/forms/tiptap/suggestion-utils";
import { DueDateDropdown } from "./dropdown/DueDateDropdown";
import { DueDateExtensionId } from "../utils";

export const DueDateExtension = Mention.extend({
  name: DueDateExtensionId,
  // Note that this extension currently doesn't insert its own nodes into the DOM.
  // parseHTML() {},
  // renderHTML(props) {},
  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() || [];
    return [...parentPlugins, dueDateDecoratorPlugin];
  },
  addOptions() {
    let isMouseInteractingWithSuggestionDropdown = false;

    return {
      ...this.parent?.(),
      // These suggestion options are defined in `@tiptap/extension-mention`
      suggestion: {
        ...this.parent?.().suggestion,
        pluginKey: new PluginKey(DueDateExtensionId),
        char: "", // This value is ignored because we are using a custom `findSuggestionMatch` function.
        allowedPrefixes: [" ", ":"], // Also ignored.
        allowSpaces: true, // Also ignored.
        allow({ editor }) {
          // Show suggestions if the editor is focused OR if the user is actively interacting with the dropdown
          // This prevents the dropdown from closing during mouse interactions while still respecting focus changes.
          return editor.isFocused || isMouseInteractingWithSuggestionDropdown;
        },
        decorationClass: "due-date-suggestion-active",
        decorationTag: "span",
        findSuggestionMatch: findDueDateSuggestionMatch,
        // This code is adapted from `@tiptap/extension-mention`, MIT license.
        // https://github.dev/ueberdosis/tiptap
        command: ({ editor, props }) => {
          const match = findDueDateSuggestionMatch({ $position: editor.state.selection.$from });
          if (!match) return;

          const range = { from: match.mention.range.from, to: match.range.to };

          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");
          if (overrideSpace) {
            range.to += 1;
          }

          // Note that, instead of inserting some kind of "due date" node, we are updating the existing
          // mention node with the due date.
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: MentionExtensionId,
                attrs: props,
              },
              {
                type: "text",
                text: " ",
              },
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
        },

        // The due date dropdown is responsible for loading the items.
        items: () => [],

        render: () => {
          let component: SuggestionEntryComponent<{}> | undefined;
          let popup: Instance[] = [];
          let editor: Editor | undefined;

          return {
            onStart: (props) => {
              const match = findDueDateSuggestionMatch({ $position: props.editor.state.selection.$from });
              if (!match) return;
              const mentionNode = match.mention.node;
              editor = props.editor;

              component = new ReactRenderer(DueDateDropdown, {
                props: {
                  ...props,
                  mention: mentionNode.attrs,
                },
                editor: props.editor,
              });

              // The provided `props.clientRect` provides a reference to the suggestion query text. If that
              // text is empty, then `props.clientRect` will be `null`. In this case we provide our own
              // reference to the cursor position.
              const getReferenceClientRect = (props.clientRect ||
                getClientRectForCursorPosition(props.editor)) as GetReferenceClientRect;

              popup = tippy("body", {
                getReferenceClientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                hideOnClick: false,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                zIndex: 150,
                onShow() {
                  // Add mouse event listeners to track interactions with the dropdown
                  const popupElement = component?.element;

                  if (!popupElement) return;

                  const handleMouseEnter = () => {
                    isMouseInteractingWithSuggestionDropdown = true;
                  };

                  const handleMouseLeave = () => {
                    isMouseInteractingWithSuggestionDropdown = false;
                  };

                  popupElement.addEventListener("mouseenter", handleMouseEnter);
                  popupElement.addEventListener("mouseleave", handleMouseLeave);

                  // Store cleanup functions for later removal
                  (popupElement as any)._dueDateCleanup = () => {
                    popupElement.removeEventListener("mouseenter", handleMouseEnter);
                    popupElement.removeEventListener("mouseleave", handleMouseLeave);
                  };
                },
                onHide() {
                  // Clean up event listeners
                  const popupElement = component?.element;

                  if (popupElement && (popupElement as any)._dueDateCleanup) {
                    (popupElement as any)._dueDateCleanup();
                    delete (popupElement as any)._dueDateCleanup;
                  }

                  isMouseInteractingWithSuggestionDropdown = false;
                },
              });
            },

            onUpdate(props) {
              if (component) {
                component?.updateProps(props);

                if (!props.clientRect) return;

                popup[0]?.setProps({
                  getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                });
              } else {
                this.onStart?.(props);
              }
            },

            onKeyDown(props) {
              if (props.event.key === "Escape") {
                // We interpret an Escape key press as "I didn't intend to start inserting a due date.
                // I just forgot to enter a space after the @@mention." So we just insert a space after
                // the mention which has the side effect of closing the due date picker.
                editor
                  ?.chain()
                  .focus()
                  .insertContentAt(props.range.from, [
                    {
                      type: "text",
                      text: " ",
                    },
                  ])
                  // By default the cursor will move to the end of the inserted text so
                  // we need to manually move the cursor back to it's original position.
                  .setTextSelection(props.range.to + 1)
                  .run();

                props.event.stopPropagation();
                return true;
              }

              return component?.ref?.onKeyDown(props) || false;
            },

            onExit() {
              // Reset mouse interaction state
              isMouseInteractingWithSuggestionDropdown = false;
              popup[0]?.destroy();
              component?.destroy();
            },
          };
        },
      },
    };
  },
});

function getClientRectForCursorPosition(editor: Editor) {
  const { view } = editor;
  const { state } = view;
  const { selection } = state;
  const { from } = selection;

  // Get the coordinates of the cursor position
  const start = view.coordsAtPos(from);

  // Return a function that provides the client rect for the tippy.js positioning
  return () => ({
    top: start.top,
    bottom: start.bottom,
    left: start.left,
    right: start.right,
    width: 0,
    height: start.bottom - start.top,
    x: start.left,
    y: start.top,
  });
}
